import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatCodeForFlag } from '@/features/translator/signals.ts';
// Removed direct flag imports
import { getCustomFlagPath } from '@/features/translator/customFlags.ts'; // Import the helper
import logger from "app/utils/logger";

type LanguageSwitcherProps = {
  width: number;
  height: number;
  sourceLanguage?: string;
  targetLanguage?: string;
  sourceTwoLetterCode?: string;
  targetTwoLetterCode?: string;
  onClick?: () => void;
}

const LanguageSwitcher = ({
  width,
  height,
  sourceLanguage, // Removed default 'en'
  targetLanguage, // Removed default 'de'
  // sourceTwoLetterCode = 'gb', // Defaults not needed if language is missing
  // targetTwoLetterCode = 'de', // Defaults not needed if language is missing
  onClick
}: LanguageSwitcherProps) => {
  const { i18n } = useTranslation();

  // Get custom flag paths or format codes for standard flags
  const sourceCustomFlagPath = sourceLanguage ? getCustomFlagPath(sourceLanguage) : null;
  const targetCustomFlagPath = targetLanguage ? getCustomFlagPath(targetLanguage) : null;
  const sourceFlagCode = sourceLanguage && !sourceCustomFlagPath ? formatCodeForFlag(sourceLanguage) : null;
  const targetFlagCode = targetLanguage && !targetCustomFlagPath ? formatCodeForFlag(targetLanguage) : null;

  const flagStyle: React.CSSProperties = { // Explicitly type as CSSProperties
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transform: "scale(1.5)" // Make flag fill the circle
  };

  const handleClick = () => {
    if (onClick) onClick();
  };

  return (
    <Box 
      onClick={handleClick}
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",        
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "white",        
        borderRadius: "50%",
        
        cursor: "pointer",
        position: "relative",
        padding: 1.5
      }}
    >
      <Box 
        sx={{ 
          position: "relative",
          width: "100%",
          height: "80%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        {/* Source Language Flag - Top left */}
        <Box 
          sx={{ 
            position: "absolute",
            top: "0%",
            left: "0%",
            width: width * 0.55, 
            height: width * 0.55,
            borderRadius: "50%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
            backgroundColor: "white", // Background for '?'
            zIndex: 8
          }}
        >
          {sourceCustomFlagPath ? (
            <img src={sourceCustomFlagPath} alt="Source Language Flag" style={flagStyle} />
          ) : sourceFlagCode ? (
            <span className={`fi fi-${sourceFlagCode}`} style={flagStyle} />
          ) : (
            <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>?</Typography>
          )}
        </Box>

        {/* Curved Arrows */}
        <Box 
          sx={{ 
            position: "absolute",
            width: "100%",
            height: "100%",
            zIndex: 1
          }}
        >
          <svg width="100%" height="100%" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Arrow from source to target */}
            <path 
              d="M35 35 C 45 55, 55 55, 65 65" 
              stroke="#333" 
              strokeWidth="3" 
              strokeLinecap="round"
              markerEnd="url(#arrowhead)"
            />
            {/* Arrow from target to source */}
            <path 
              d="M65 65 C 55 45, 45 45, 35 35" 
              stroke="#333" 
              strokeWidth="3" 
              strokeLinecap="round"
              markerEnd="url(#arrowhead)"
            />
            {/* Arrow marker definition */}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon points="0 0, 10 3.5, 0 7" fill="#333" />
              </marker>
            </defs>
          </svg>
        </Box>

        {/* Target Language Flag - Bottom right */}
        <Box 
          sx={{ 
            position: "absolute",
            bottom: "0%",
            right: "10%",
            width: width * 0.4, 
            height: width * 0.4,
            borderRadius: "50%",
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.2)",
            backgroundColor: "white", // Background for '?'
            zIndex: 2
          }}
        >
          {targetCustomFlagPath ? (
            <img src={targetCustomFlagPath} alt="Target Language Flag" style={flagStyle} />
          ) : targetFlagCode ? (
            <span className={`fi fi-${targetFlagCode}`} style={flagStyle} />
          ) : (
            <Typography variant="h6" sx={{ color: 'black', fontWeight: 'bold' }}>?</Typography>
          )}
        </Box>
      </Box>


    </Box>
  );
};

export default LanguageSwitcher;
